import {
  css,
  generateUtilityClasses,
  tableBodyClasses as muiTableBodyClasses,
  tableCellClasses as muiTableCellClasses,
  tableHeadClasses as muiTableHeadClasses,
  tableRowClasses as muiTableRowClasses,
} from '@mui/material';

import { tableClasses } from '../Table/theme';
import { tableContainerClasses } from '../TableContainer/theme';
import { tableRowClasses } from '../TableRow';

import type { ThemeOptions } from '@mui/material';

// generateUtilityClasses가 이미 타입을 제공하므로 별도 인터페이스 불필요
export const tableCellClasses = generateUtilityClasses('TableCell', [
  'root',
  'content',
  'endAdornment',
  'startAdornment',
  'sticky',
  'stickyLeft',
  'stickyRight',
  'resizeHandle',
  'resizingIndicator',
]);

// ✅ TableCell 컴포넌트의 styleOverrides는 제거됨 (컴포넌트로 이전)
// ✅ module augmentation 완전 제거 (가이드 3-2 패턴 적용)
// ⚠️ MuiTableCell은 MUI 컴포넌트 직접 확장이므로 유지 (가이드 6-1)
export const tableCellThemes: ThemeOptions['components'] = {
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => css`
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        vertical-align: middle;
        background-color: ${theme.color['surface/default']};

        // 테이블 행 아래 border를 cell에 표시되도록 적용
        .${muiTableBodyClasses.root} > .${muiTableRowClasses.root} > & {
          border-bottom: ${theme.mixins.border('border/weaker/default')};
        }

        // 마지막 행은 border 없음 (table 아래쪽 border가 있음)
        .${muiTableBodyClasses.root} > .${muiTableRowClasses.root}:last-of-type > &,
        // 마지막 행이 expandable 행이고 닫혀 있을때 border 없음
        .${muiTableBodyClasses.root}
          > .${muiTableRowClasses.root}.${tableRowClasses.collapse}:not(.${tableRowClasses.open})
          > &,
        // 마지막 행이 expandable 행이고 닫혀있으면 그 다음 마지막 행도 border 없음
        .${muiTableBodyClasses.root}
          > :has(
            .${muiTableRowClasses.root}.${tableRowClasses.collapse}:last-of-type:not(.${tableRowClasses.open})
          )
          .${muiTableRowClasses.root}:nth-last-of-type(2)
          > & {
          border-bottom-width: 0px;
        }

        // Expanded 영역의 스타일
        .${tableRowClasses.collapse} > & {
          background-color: ${theme.color['surface/select']};

          &:is(.${muiTableCellClasses.sizeMedium}, .${muiTableCellClasses.sizeSmall}) {
            height: auto;
          }
        }

        // subRow(하위 row) 영역의 스타일
        // 셀이 row를 빈틈없이 채우므로 row 전체가 하나의 background로 보인다.
        // &&& 로 body 슬롯의 hover/selected background(클래스 3개)보다 명시도를 높인다.
        .${tableRowClasses.subRow} > &&& {
          background-color: ${theme.color['surface/select']};
        }

        &:has(> .${tableCellClasses.sticky}) {
          position: sticky;

          &:has(> .${tableCellClasses.stickyLeft}) {
            left: 0;

            ::after {
              display: none;
              pointer-events: none;
              content: '';
              position: absolute;
              top: 0px;
              bottom: 0px;
              right: 0px;
              width: 0px;
              background: linear-gradient(to right, rgba(19, 22, 25, 0.05), rgba(0, 0, 0, 0));
              transition:
                right 0.2s ease-in-out,
                width 0.2s ease-in-out;
            }

            .${tableContainerClasses}[data-is-scroll-start="false"] &::after {
              right: -8px;
              width: 8px;
            }

            &:not(:has(+ * > .${tableCellClasses.stickyLeft})) {
              ::after {
                display: block;
              }
            }
          }

          &:has(> .${tableCellClasses.stickyRight}) {
            right: 0;

            ::before {
              pointer-events: none;
              content: '';
              position: absolute;
              top: 0px;
              bottom: 0px;
              left: 0px;
              width: 0px;
              background: linear-gradient(to left, rgba(19, 22, 25, 0.05), rgba(0, 0, 0, 0));
              transition:
                left 0.2s ease-in-out,
                width 0.2s ease-in-out;
            }

            .${tableContainerClasses}[data-is-scroll-end="false"] &::before {
              left: -8px;
              width: 8px;
            }

            & + :has(> .${tableCellClasses.stickyRight}) {
              ::before {
                display: none;
              }
            }
          }
        }
      `,
      head: ({ theme }) => css`
        height: 40px;
        padding: 4px 16px;
        letter-spacing: unset;

        & .${tableCellClasses.content} {
          color: ${theme.color['text/weaker/default']};
          ${theme.typography['label/semibold/sm']};
          white-space: nowrap;
        }

        &.${muiTableCellClasses.paddingCheckbox} {
          line-height: 16px;
        }

        &.${muiTableCellClasses.sizeSmall}, &.${muiTableCellClasses.sizeMedium} {
          height: 40px;
          padding: 4px 16px;
        }

        .${tableClasses.root}[data-show-side-margin='true']
          > .${muiTableHeadClasses.root}
          > .${muiTableRowClasses.root}
          > & {
          &:first-of-type {
            padding-left: calc(16px + 20px);
          }
          &:last-of-type {
            padding-right: calc(16px + 20px);
          }
          &.${muiTableCellClasses.paddingNone}:first-of-type {
            padding-left: calc(8px + 20px);
          }
          &.${muiTableCellClasses.paddingNone}:last-of-type {
            padding-right: calc(8px + 20px);
          }
        }

        ${`&:has(> .${tableCellClasses.sticky})`} {
          z-index: ${theme.zIndex.sticky + 2};
        }
      `,
      body: ({ theme }) => css`
        position: relative;

        & .${tableCellClasses.content} {
          color: ${theme.color['text/default']};
          ${theme.typography['label/regular/md']};
        }

        &.${muiTableCellClasses.sizeSmall} {
          height: 40px;
          padding: 4px 16px;
        }

        &.${muiTableCellClasses.sizeMedium} {
          height: 48px;
          padding: 8px 16px;
        }

        .${tableClasses.root}[data-show-side-margin='true']
          > .${muiTableBodyClasses.root}
          > .${muiTableRowClasses.root}
          > & {
          &:first-of-type {
            padding-left: calc(16px + 20px);
          }
          &:last-of-type {
            padding-right: calc(16px + 20px);
          }
          &.${muiTableCellClasses.paddingNone}:first-of-type {
            padding-left: calc(8px + 20px);
          }
          &.${muiTableCellClasses.paddingNone}:last-of-type {
            padding-right: calc(8px + 20px);
          }
        }

        .${tableClasses.root}
          > .${muiTableBodyClasses.root}
          > .${muiTableRowClasses.root}.${tableRowClasses.collapse}
          > & {
          padding: 0;
        }
        .${tableRowClasses.collapseContent} {
          padding: 20px;
        }
        .${tableClasses.root}[data-show-side-margin='true']
          > .${muiTableBodyClasses.root}
          > .${muiTableRowClasses.root}.${tableRowClasses.collapse}
          > & {
          padding: 0;
        }
        .${tableClasses.root}[data-show-side-margin='true'] & .${tableRowClasses.collapseContent} {
          padding: 24px 32px;
        }

        &.${muiTableCellClasses.paddingCheckbox} {
          line-height: 16px;
        }

        &.${muiTableCellClasses.paddingNone} {
          padding: 0 8px;
        }

        .${muiTableRowClasses.hover}:hover & {
          background-color: ${theme.color['surface/hover']};
        }
        .${muiTableRowClasses.selected} & {
          background-color: ${theme.color['surface/select']};
        }

        ${`&:has(> .${tableCellClasses.sticky})`} {
          z-index: ${theme.zIndex.sticky + 1};
        }
      `,
      footer: () => css``,
      sizeSmall: () => css``,
      sizeMedium: () => css``,
      paddingCheckbox: () => css`
        width: 48px;
      `,
      paddingNone: () => css`
        width: 48px;
      `,
      alignLeft: () => css``,
      alignCenter: () => css``,
      alignRight: () => css``,
      alignJustify: () => css``,
      stickyHeader: () => css``,
    },
  },
};
